import {
  Button,
  Flex,
  FormControl,
  Select,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BranchTermKind,
  branchTermsApi,
} from '@texas/api/endpoints/branchesApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { ConfigurationPage } from '@texas/components/configuration/layout/ConfigurationPage';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { useActiveSession } from '@texas/hooks/useSession';
import { BranchTerm, BranchTermModal } from './BranchTermModal';
import {
  getConfigurationBranchId,
  setConfigurationBranchId,
} from '../configurationBranch';

interface BranchTermsProps {
  kind: BranchTermKind;
}

const translationKeys: Record<BranchTermKind, string> = {
  deliveryMethods: 'configuration.navigation.deliveryMethod',
  deliveryTerms: 'configuration.navigation.deliveryTerms',
  paymentTerms: 'configuration.navigation.paymentTerms',
};

export function BranchTerms({ kind }: BranchTermsProps) {
  const { t } = useTranslation();
  const { branches } = useActiveSession();
  const [selectedBranchId, setSelectedBranchId] = useState(() =>
    getConfigurationBranchId(branches),
  );
  const [filter, setFilter] = useState('');
  const [selectedTerm, setSelectedTerm] = useState<BranchTerm | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data, refetch, loading, error } = useApiResource(
    branchTermsApi.getAll,
  );
  const title = t(translationKeys[kind] as 'configuration.navigation.garp');

  useEffect(() => {
    if (selectedBranchId) {
      refetch(kind, selectedBranchId);
    }
  }, [kind, refetch, selectedBranchId]);

  const terms = useMemo(() => {
    const normalizedFilter = filter.trim().toLocaleLowerCase();
    return (data ?? [])
      .filter((term) => !term.archived)
      .filter(
        (term) =>
          !normalizedFilter ||
          term.name.toLocaleLowerCase().includes(normalizedFilter) ||
          term.identifier.toLocaleLowerCase().includes(normalizedFilter),
      )
      .sort((left, right) => left.displayOrder - right.displayOrder);
  }, [data, filter]);

  return (
    <ConfigurationPage
      title={title}
      toolbar={
        <>
          <FormControl w="240px">
            <Select
              aria-label={t('general.branch')}
              h="44px"
              variant="entityDirectory"
              value={selectedBranchId}
              onChange={(event) => {
                const branchId = Number(event.target.value);
                setConfigurationBranchId(branchId);
                setSelectedBranchId(branchId);
              }}
            >
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </Select>
          </FormControl>
          <SearchFilterInput
            value={filter}
            style="entityDirectory"
            onChange={setFilter}
          />
        </>
      }
      action={
        <Button
          variant="entityDirectoryPrimary"
          onClick={() => {
            setSelectedTerm(null);
            onOpen();
          }}
        >
          {t('general.new')} {title.toLocaleLowerCase()}
        </Button>
      }
    >
      <BranchTermModal
        kind={kind}
        branchId={selectedBranchId}
        term={selectedTerm}
        isOpen={isOpen}
        onClose={onClose}
        onSaved={() => refetch(kind, selectedBranchId)}
      />

      <Flex direction="column" gap="4">
        <ErrorDetails error={error} />
        <Table variant="entity-directory">
          <Thead>
            <Tr>
              <Th>{t('general.name')}</Th>
              <Th>{t('configuration.identifier')}</Th>
              <Th>{t('branch.displayOrder')}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {!loading && terms.length === 0 && (
              <Tr>
                <Td colSpan={3}>{t('general.noData')}</Td>
              </Tr>
            )}
            {terms.map((term) => (
              <Tr
                key={term.id}
                cursor="pointer"
                role="button"
                tabIndex={0}
                onClick={() => {
                  setSelectedTerm(term);
                  onOpen();
                }}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  setSelectedTerm(term);
                  onOpen();
                }}
              >
                <Td>{term.name}</Td>
                <Td>{term.identifier}</Td>
                <Td>{term.displayOrder}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Flex>
    </ConfigurationPage>
  );
}
